import React from 'react';
import { 
  Users, Trophy, Clock, Heart, 
  Leaf, Star, ShieldCheck, Mail, Phone 
} from 'lucide-react';

const stats = [
  { icon: <Trophy size={32} />, value: '300+', label: 'Projekte erfolgreich umgesetzt' },
  { icon: <Users size={32} />, value: '220+', label: 'Zufriedene Kunden' },
  { icon: <Clock size={32} />, value: '10+', label: 'Jahre Erfahrung' },
  { icon: <Heart size={32} />, value: '100%', label: 'Engagement' },
];

const About = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      
      {/* HERO */}
      <header className="relative w-full h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/20069743/pexels-photo-20069743.jpeg?_gl=1*1xjr7m5*_ga*NDIwOTEzMDMyLjE3NjkzNTY0Mjk.*_ga_8JE65Q40S6*czE3NzA2NzI5NTYkbzIkZzEkdDE3NzA2NzMxNzkkajUzJGwwJGgw" 
            alt="Palloshigalabau Gartenbau" 
            className="w-full h-full object-cover scale-105"
          />
          <div className="absolute inset-0 bg-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 uppercase">
            Über <span className="text-emerald-500">Uns</span>
          </h1>
          <p className="text-xl text-slate-200 max-w-3xl mx-auto">
            Palloshigalabau – Ihr zuverlässiger Partner für Garten- und Landschaftsbau  
            in Deutschland .
          </p>
        </div>
      </header>

      {/* STORY */}
      <section className="py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <h2 className="text-emerald-700 font-bold uppercase tracking-widest text-sm">
              Unsere Geschichte
            </h2>
            <h3 className="text-4xl md:text-5xl font-black">
              Erfahrung, die man sieht. <br/>
              <span className="text-emerald-600">Qualität, die bleibt.</span>
            </h3>
            <div className="space-y-4 text-slate-600 text-lg">
              <p>
                <strong>Palloshigalabau</strong> wurde mit dem Anspruch gegründet,
                hochwertige und zuverlässige Leistungen im Garten- und Landschaftsbau
                anzubieten.
              </p>
              <p>
                Heute betreuen wir private und gewerbliche Kunden in
                <strong> Deutschland </strong> und realisieren Projekte
                wie <strong>Baumfällen</strong>, <strong>Gartenpflege</strong>,
                <strong> Zaunbau</strong>, <strong>Pflasterarbeiten</strong> und
                <strong> Holzterrassen</strong>.
              </p>
              <p>
                Jedes Projekt wird individuell geplant und mit höchster
                handwerklicher Präzision umgesetzt.
              </p>
            </div>
          </div>

          <img 
            src="/img/garden.jpg" 
            alt="Palloshigalabau Team bei der Arbeit" 
            className="rounded-[2rem] shadow-2xl border-8 border-white"
          />
        </div>
      </section>

      {/* STATS */}
      <section className="bg-slate-900 py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
            {stats.map((stat, i) => (
              <div key={i}>
                <div className="text-emerald-500 mb-4 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-4xl font-black text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400 uppercase text-xs tracking-widest">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-slate-50 py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-emerald-700 font-bold uppercase text-sm mb-2">
              Wofür wir stehen
            </h2>
            <h3 className="text-4xl font-black">Unsere Werte</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-white p-10 rounded-[2rem] shadow-sm">
              <Leaf className="text-emerald-600 mb-6" size={40} />
              <h3 className="text-2xl font-bold mb-4">Nachhaltigkeit</h3>
              <p className="text-slate-600 text-lg">
                Umweltbewusstes Arbeiten, langlebige Materialien
                und nachhaltige Lösungen.
              </p>
            </div>

            <div className="bg-white p-10 rounded-[2rem] shadow-sm">
              <ShieldCheck className="text-emerald-600 mb-6" size={40} />
              <h3 className="text-2xl font-bold mb-4">Qualität</h3>
              <p className="text-slate-600 text-lg">
                Präzise Ausführung vom ersten Arbeitsschritt
                bis zur finalen Übergabe.
              </p>
            </div>

            <div className="bg-white p-10 rounded-[2rem] shadow-sm">
              <Star className="text-emerald-600 mb-6" size={40} />
              <h3 className="text-2xl font-bold mb-4">Zuverlässigkeit</h3>
              <p className="text-slate-600 text-lg">
                Termintreue, transparente Kommunikation
                und faire Angebote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-[1600px] mx-auto bg-slate-900 rounded-[2.5rem] py-24 px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-8">
            Bereit für Ihr <span className="text-emerald-500">Projekt?</span>
          </h2>
          <p className="text-slate-300 text-xl mb-12">
            Palloshigalabau – Qualität im Garten- und Landschaftsbau
            in Deutschland 
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button className="bg-emerald-600 text-white px-10 py-5 rounded-2xl font-bold text-xl flex items-center gap-3">
              <Mail size={24} /> Angebot anfordern
            </button>
            <a href="tel:+4915735547097" className="bg-white/10 text-white px-10 py-5 rounded-2xl font-bold text-xl flex items-center gap-3">
              <Phone size={24} /> +49 15735547097
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
